// src/di/dependency-registry.ts
var DependencyRegistry = class {
  _dependencies = {};
  constructor() {
    this.register = this.register.bind(this);
    this.resolve = this.resolve.bind(this);
    this.getDIContainer = this.getDIContainer.bind(this);
  }
  register(name, dependency) {
    if (this._dependencies[name]) {
      console.warn(
        `Dependency "${String(name)}" already exists in dependency registry`
      );
    }
    this._dependencies[name] = dependency.create(this.resolve);
  }
  resolve(name) {
    if (!this._dependencies[name]) {
      throw new Error(`Dependency "${String(name)}" not found in container`);
    }
    return this._dependencies[name];
  }
  getDIContainer() {
    return this._dependencies;
  }
};

// src/errors/api.error.ts
var ApiError = class extends Error {
  constructor(message, statusCode) {
    super(message);
    this.statusCode = statusCode;
  }
  statusCode;
};

// src/enums.ts
var MessageSource = /* @__PURE__ */ ((MessageSource2) => {
  MessageSource2["User"] = "user";
  MessageSource2["Llm"] = "llm";
  MessageSource2["Tool"] = "tool";
  MessageSource2["System"] = "system";
  return MessageSource2;
})(MessageSource || {});
var ScheduledTaskStatus = /* @__PURE__ */ ((ScheduledTaskStatus2) => {
  ScheduledTaskStatus2["Active"] = "active";
  ScheduledTaskStatus2["Paused"] = "paused";
  ScheduledTaskStatus2["Deleted"] = "deleted";
  return ScheduledTaskStatus2;
})(ScheduledTaskStatus || {});

// src/schemas/admin.ts
import { z } from "zod";
var AdminRoleSchema = z.enum(["viewer", "super_admin"]);
var AdminSchema = z.object({
  id: z.string(),
  userId: z.string(),
  role: AdminRoleSchema,
  createdAt: z.string(),
  createdBy: z.string().nullable()
});
var UserDayStatSchema = z.object({
  date: z.string(),
  count: z.number()
});
var UserStatsSchema = z.object({
  total: z.number(),
  totalMessages: z.number(),
  totalChats: z.number(),
  activeSubscriptions: z.number(),
  registeredComputers: z.number(),
  byDay: z.array(UserDayStatSchema),
  activeByDay: z.array(UserDayStatSchema),
  messagesByDay: z.array(UserDayStatSchema),
  chatsByDay: z.array(UserDayStatSchema)
});

// src/schemas/auth.ts
import { z as z2 } from "zod";
var AuthSchema = z2.object({
  walletAddress: z2.string(),
  signature: z2.string(),
  message: z2.string()
});
var RefreshSchema = z2.object({
  refreshToken: z2.string()
});
var AuthTokensSchema = z2.object({
  accessToken: z2.string(),
  refreshToken: z2.string()
});

// src/schemas/chat.ts
import z3 from "zod";
var ChatSchema = z3.object({
  id: z3.string(),
  title: z3.string(),
  userId: z3.string(),
  createdAt: z3.string()
});
var GetChatsQuerySchema = z3.object({
  limit: z3.coerce.number().min(1).max(50).default(50),
  offset: z3.coerce.number().min(0).default(0)
});
var GetChatParamsSchema = z3.object({
  chatId: z3.string().nonempty()
});
var GetMessagesParamsSchema = z3.object({
  chatId: z3.string().nonempty()
});
var GetMessagesQuerySchema = z3.object({
  limit: z3.coerce.number().min(1).max(50).default(50),
  offset: z3.coerce.number().min(0).default(0)
});
var CreateChatSchema = z3.object({
  title: z3.string().min(1)
});
var DeleteChatParamsSchema = z3.object({
  chatId: z3.string().nonempty()
});
var UpdateChatParamsSchema = z3.object({
  chatId: z3.string().nonempty()
});
var UpdateChatSchema = z3.object({
  title: z3.string().min(1)
});
var CreateMessageParamsSchema = z3.object({
  chatId: z3.string().nonempty()
});
var CreateMessageSchema = z3.object({
  prompt: z3.string().min(1)
});

// src/schemas/computer.ts
import z4 from "zod";
var ComputerStatusSchema = z4.enum(["online", "offline"]);
var ComputerSchema = z4.object({
  id: z4.string(),
  os: z4.string(),
  ip: z4.string(),
  name: z4.string(),
  description: z4.string().optional(),
  status: ComputerStatusSchema,
  userId: z4.string(),
  createdAt: z4.string(),
  updatedAt: z4.string()
});

// src/schemas/news.ts
import z5 from "zod";
var RecentNewsQuerySchema = z5.object({
  amount: z5.coerce.number().int().min(1).max(50).default(3)
});
var NewsSchema = z5.object({
  id: z5.number(),
  hash_id: z5.string(),
  title: z5.string(),
  content: z5.string(),
  source_url: z5.string().optional(),
  published_at: z5.string(),
  fetched_at: z5.string(),
  processed_at: z5.string().optional(),
  status: z5.string(),
  is_important: z5.boolean(),
  analysis: z5.string().optional()
});

// src/schemas/scheduled-task.ts
import z6 from "zod";
var CreateScheduledTaskSchema = z6.object({
  prompt: z6.string(),
  cronExpr: z6.string(),
  label: z6.string(),
  timezone: z6.string().optional().default("UTC")
});
var UpdateScheduledTaskParamsSchema = z6.object({
  taskId: z6.string().uuid()
});
var UpdateScheduledTaskSchema = z6.object({
  prompt: z6.string().optional(),
  cronExpr: z6.string().optional(),
  label: z6.string().optional(),
  status: z6.enum(["active" /* Active */, "paused" /* Paused */]).optional(),
  timezone: z6.string().optional()
});
var DeleteScheduledTaskParamsSchema = z6.object({
  taskId: z6.string().uuid()
});
var ScheduledTaskListItemSchema = z6.object({
  id: z6.string(),
  label: z6.string().optional(),
  prompt: z6.string(),
  cronExpr: z6.string(),
  status: z6.nativeEnum(ScheduledTaskStatus),
  nextRunAt: z6.string(),
  lastRunAt: z6.string().nullable(),
  runCount: z6.number(),
  createdAt: z6.string()
});

// src/schemas/subscription.ts
import { z as z7 } from "zod";
var SubscriptionSchema = z7.object({
  id: z7.string(),
  userId: z7.string(),
  status: z7.string(),
  plan: z7.string(),
  txHash: z7.string(),
  walletAddress: z7.string(),
  expiresAt: z7.string(),
  createdAt: z7.string()
});
var SubscriptionPlanSchema = z7.object({
  id: z7.string(),
  name: z7.string(),
  priceWei: z7.string(),
  durationDays: z7.number(),
  usdtAddress: z7.string(),
  treasuryAddress: z7.string(),
  chainId: z7.number()
});
var VerifyPaymentSchema = z7.object({
  txHash: z7.string().regex(/^0x[a-fA-F0-9]{64}$/, "Invalid transaction hash"),
  walletAddress: z7.string().regex(/^0x[a-fA-F0-9]{40}$/, "Invalid wallet address")
});

// src/schemas/user-context.ts
import z8 from "zod";
var MAX_USER_CONTEXT_LENGTH = 16e3;
var UpsertUserContextSchema = z8.object({
  content: z8.string().max(MAX_USER_CONTEXT_LENGTH)
});
var UserContextSchema = z8.object({
  content: z8.string().nullable()
});
var UpdatedUserContextSchema = z8.object({
  content: z8.string()
});
var AppendUserContextResultSchema = z8.object({
  content: z8.string()
});
var UserContextHistoryItemSchema = z8.object({
  id: z8.string(),
  content: z8.string(),
  source: z8.enum(["llm", "user"]),
  createdAt: z8.string()
});

// src/schemas/user.ts
import { z as z9 } from "zod";
var UserSchema = z9.object({
  id: z9.string(),
  walletAddress: z9.string(),
  createdAt: z9.string()
});

// src/types/socket.ts
var SocketEvent = /* @__PURE__ */ ((SocketEvent2) => {
  SocketEvent2["NewMessage"] = "new_message";
  SocketEvent2["Typing"] = "typing";
  SocketEvent2["ChatCreated"] = "chat_created";
  SocketEvent2["ChatDeleted"] = "chat_deleted";
  SocketEvent2["ComputerCreated"] = "computer_created";
  SocketEvent2["ComputerStatusUpdated"] = "computer_status_updated";
  SocketEvent2["TaskComplete"] = "task_complete";
  SocketEvent2["TaskCancelled"] = "task_cancelled";
  SocketEvent2["CancelTask"] = "cancel_task";
  return SocketEvent2;
})(SocketEvent || {});
var ComputerSocketEvent = /* @__PURE__ */ ((ComputerSocketEvent2) => {
  ComputerSocketEvent2["Init"] = "init";
  ComputerSocketEvent2["RunTool"] = "run_tool";
  ComputerSocketEvent2["ToolResult"] = "tool_result";
  return ComputerSocketEvent2;
})(ComputerSocketEvent || {});

// src/api/index.ts
import axios, {
  AxiosError
} from "axios";
function createCitApi(baseURL, config, onAuthError) {
  const client = axios.create({
    baseURL,
    ...config
  });
  let token = null;
  client.interceptors.request.use((config2) => {
    if (token) {
      config2.headers.Authorization = `Bearer ${token}`;
    }
    return config2;
  });
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401 && onAuthError) {
        onAuthError();
      }
      return Promise.reject(error);
    }
  );
  function setToken(newToken) {
    token = newToken;
  }
  async function authenticate(data) {
    const response = await client.post("/auth/login", data);
    return response.data;
  }
  async function refresh(data) {
    const response = await client.post("/auth/refresh", data);
    return response.data;
  }
  async function createMessage(data) {
    const response = await client.post("/messages", data);
    return response.data;
  }
  async function getMessages(limit = 50, offset = 0) {
    const response = await client.get("/messages", {
      params: { limit, offset }
    });
    return response.data;
  }
  async function getComputers() {
    const response = await client.get("/computers");
    return response.data;
  }
  async function createSetupToken() {
    const response = await client.post("/computers/token");
    return response.data.token;
  }
  return {
    setToken,
    authenticate,
    refresh,
    createMessage,
    getMessages,
    createSetupToken,
    getComputers
  };
}
export {
  AdminRoleSchema,
  AdminSchema,
  ApiError,
  AppendUserContextResultSchema,
  AuthSchema,
  AuthTokensSchema,
  AxiosError,
  ChatSchema,
  ComputerSchema,
  ComputerSocketEvent,
  ComputerStatusSchema,
  CreateChatSchema,
  CreateMessageParamsSchema,
  CreateMessageSchema,
  CreateScheduledTaskSchema,
  DeleteChatParamsSchema,
  DeleteScheduledTaskParamsSchema,
  DependencyRegistry,
  GetChatParamsSchema,
  GetChatsQuerySchema,
  GetMessagesParamsSchema,
  GetMessagesQuerySchema,
  MessageSource,
  NewsSchema,
  RecentNewsQuerySchema,
  RefreshSchema,
  ScheduledTaskListItemSchema,
  ScheduledTaskStatus,
  SocketEvent,
  SubscriptionPlanSchema,
  SubscriptionSchema,
  UpdateChatParamsSchema,
  UpdateChatSchema,
  UpdateScheduledTaskParamsSchema,
  UpdateScheduledTaskSchema,
  UpdatedUserContextSchema,
  UpsertUserContextSchema,
  UserContextHistoryItemSchema,
  UserContextSchema,
  UserDayStatSchema,
  UserSchema,
  UserStatsSchema,
  VerifyPaymentSchema,
  createCitApi
};
